import { nearestSixteenth, Panel, PanelItem } from '@rta/rta-blueprint-library';

const PASSTHROUGH_SKUS = ['PASSTHROUGH', 'MOK-PASSTHROUGH'];

type XSpan = { id: string; left: number; right: number; boundToEdge: number };

type IntersectionLike = {
	intersectionReferenceIds: Array<string>;
	size: { width: number };
	shouldHide?: boolean;
};

export type FilterPassthroughDimensionsParams<T extends IntersectionLike> = {
	panel: Panel;
	areas: Array<T>;
	inside: boolean;
};

// Matches the x-extent derivation in get-horizontal-intersection-areas.cfn so spans line up exactly
const getXSpan = (panelItem: PanelItem, inside: boolean): XSpan => {
	const { front: corners } = panelItem.getCorners(inside);
	const groupCenterX = panelItem.refMap?.panelItemGroup?.center.x ?? 0;
	const isCircular = panelItem.item.sku === 'MOK-PASSTHROUGH';

	return {
		id: panelItem.id,
		left: isCircular ? nearestSixteenth(groupCenterX) : nearestSixteenth(corners[0].x + groupCenterX),
		right: isCircular ? nearestSixteenth(groupCenterX) : nearestSixteenth(corners[1].x + groupCenterX),
		boundToEdge: panelItem.boundToEdge
	};
};

/**
 * A passthrough needs exactly one horizontal dimension VISIBLE by default -- the distance to the
 * nearest side edge of its panel. It gets its size from the 4" marker drawn above the cutout, so
 * every other horizontal dimension involving it is noise and is dropped, EXCEPT the opposite
 * edge-to-panel dim (GitHub #601): that one is kept with `shouldHide: true` so `DimensionLine`
 * still persists a `Dimension` record for it -- "Show Hidden Dimensions" then surfaces it faded,
 * and the existing right-click "Show Dimension" (hideDimensionMenuItem) lets the user turn it on
 * permanently. Without this, there was nothing to right-click and no way to reveal the far side.
 *
 * When a vent is aligned same-side with the passthrough (both measure the identical distance to
 * that panel edge), the two would draw on top of each other -- exactly one must survive, and it's
 * always the passthrough's: the vent's matching-side dimension is dropped instead, never the
 * passthrough's, and never both. `intersectionReferenceIds` is always [leftward entity, rightward
 * entity] (see get-horizontal-intersection-areas.cfn), so a vent's own edge-to-panel entry on a
 * given side is identifiable by which slot holds `panel.id`.
 */
export const filterPassthroughDimensions = <T extends IntersectionLike>(params: FilterPassthroughDimensionsParams<T>): Array<T> => {
	const { panel, areas, inside } = params;

	const passthroughs: Array<XSpan> = [];
	const vents: Array<XSpan> = [];

	panel.panelItemGroups.children.forEach((group) => {
		group.items.children.forEach((panelItem) => {
			if (PASSTHROUGH_SKUS.includes(panelItem.item.sku)) passthroughs.push(getXSpan(panelItem, inside));
			else if (panelItem.item.category?.isVent) vents.push(getXSpan(panelItem, inside));
		});
	});

	if (passthroughs.length === 0) return areas;

	const ventIds = new Set(vents.map((vent) => vent.id));
	const drop = new Set<T>();

	passthroughs.forEach((passthrough) => {
		// Areas that also reference a vent are the vent's own intersection dimension (e.g. a dragged pass-through sits between the vent and the panel edge, so the vent measures to the pass-through) -- never drop those.
		const mine = areas.filter((area) => area.intersectionReferenceIds.includes(passthrough.id) && !area.intersectionReferenceIds.some((id) => ventIds.has(id)));
		if (mine.length === 0) return;

		// Same-side alignment only -- left-with-left or right-with-right is a genuine coinciding distance.
		// A merely touching/adjacent edge (passthrough's left meeting a vent's right, or vice versa) is a
		// different, real measurement and must never be treated as a duplicate to collapse.
		// Vents bound to a different edge (top vs bottom) render their edge-to-panel dim at a different Y
		// than the passthrough's, so they don't coincide -- only same-edge vents can visually overlap.
		vents.forEach((vent) => {
			if (vent.boundToEdge !== passthrough.boundToEdge) return;
			if (passthrough.left === vent.left) {
				const ventLeftDim = areas.find((area) => area.intersectionReferenceIds[0] === panel.id && area.intersectionReferenceIds[1] === vent.id);
				if (ventLeftDim) drop.add(ventLeftDim);
			}
			if (passthrough.right === vent.right) {
				const ventRightDim = areas.find((area) => area.intersectionReferenceIds[0] === vent.id && area.intersectionReferenceIds[1] === panel.id);
				if (ventRightDim) drop.add(ventRightDim);
			}
		});

		// Prefer the edge-to-panel dimension, but fall back to the smallest of whatever's left so a
		// dragged pass-through with nothing directly between it and the panel edge still keeps one dimension.
		const toPanelEdge = mine.filter((area) => area.intersectionReferenceIds.includes(panel.id));
		const nearestCandidates = toPanelEdge.length > 0 ? toPanelEdge : mine;
		const nearest = nearestCandidates.reduce<T | null>((closest, area) => (closest === null || area.size.width < closest.size.width ? area : closest), null);

		mine.forEach((area) => {
			if (area === nearest) return;
			// The opposite edge-to-panel dim stays in the list, hidden by default, so the user can right-click to reveal it (#601).
			if (area.intersectionReferenceIds.includes(panel.id)) {
				area.shouldHide = true;
				return;
			}
			drop.add(area);
		});
	});

	return areas.filter((area) => !drop.has(area));
};
